import React from 'react';
import { graphql, Link as GatsbyLink, useStaticQuery } from 'gatsby';
import { useIntl } from 'react-intl';

import { GridContainer, GridItem } from '@entur/grid';
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TwitterIcon,
} from '@entur/icons';
import { Contrast } from '@entur/layout';
import { Heading3, Link, Link as LinkText, Paragraph } from '@entur/typography';

import * as ROUTES from '~/lib/routes';

import './Footer.scss';

export const Footer = () => {
  const { formatMessage: f } = useIntl();
  const data = useStaticQuery(graphql`
    query {
      logo: file(relativePath: { eq: "LogoNegative.svg" }) {
        publicURL
      }
    }
  `);

  return (
    <Contrast as="footer" className="omtur-footer footer-grid">
      <div className="omtur-footer__content-wrapper">
        <div className="omtur-footer__header">
          <img
            alt=""
            aria-hidden="true"
            src={data.logo.publicURL}
            width="104"
          />
        </div>

        <GridContainer
          spacing="medium"
          as={Contrast}
          className="omtur-footer__grid"
        >
          <GridItem
            small={12}
            medium={3}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              Entur AS
            </Heading3>
            <Paragraph margin="none">
              Rådhusgata 5, 0151 Oslo
              <br />
              Postboks 1554 Vika, 0117 Oslo
            </Paragraph>
            <Paragraph margin="none">
              {f({ id: 'footer.orgnr' })}: <br /> 917 422 575
            </Paragraph>
            <LinkText
              as={GatsbyLink}
              to={ROUTES.kontaktOss.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.kontaktOss.nameAlt })}
            </LinkText>
            <LinkText
              className="omtur-footer__link"
              href="mailto:webredaktor@entur.org"
              target="_blank"
              external
            >
              {f({ id: 'footer.webredaktor' })}
            </LinkText>
          </GridItem>
          <GridItem
            small={12}
            medium={3}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              {f({ id: 'footer.informasjon' })}
            </Heading3>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.nettstedkart.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.nettstedkart.name })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.personvern.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.personvern.name })}
            </LinkText>

            <LinkText
              onClick={() => {
                if (typeof UC_UI !== 'undefined') UC_UI?.showFirstLayer();
              }}
              className="omtur-footer__link"
            >
              {f({ id: 'footer.cookieSettings' })}
            </LinkText>

            <LinkText
              className="omtur-footer__link"
              href="https://uustatus.no/nb/erklaringer/publisert/5aa4d523-635f-41a3-9a9e-f794e8404777"
              target="_blank"
              external
            >
              {f({ id: 'footer.tilgjengelighetserklaering' })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.vilkar.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.vilkar.name })}{' '}
            </LinkText>
          </GridItem>
          <GridItem
            small={12}
            medium={3}
            className="omtur-footer__vertical-section"
          >
            <Heading3 as="h2" margin="bottom">
              {f({ id: 'footer.tjenester' })}
            </Heading3>

            <LinkText
              href={ROUTES.tjenester.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.tjenester.name })}
            </LinkText>

            <LinkText
              as={GatsbyLink}
              to={ROUTES.lastnedapp.url}
              className="omtur-footer__link"
            >
              {f({ id: ROUTES.lastnedapp.name })}
            </LinkText>

            <LinkText
              href={ROUTES.datakatalog.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.datakatalog.name })}
            </LinkText>

            <LinkText
              href={ROUTES.designPortal.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.designPortal.name })}
            </LinkText>

            <LinkText
              href={ROUTES.utviklerportal.url}
              className="omtur-footer__link"
              external
            >
              {f({ id: ROUTES.utviklerportal.name })}
            </LinkText>

            <LinkText
              className="omtur-footer__link"
              href={ROUTES.incidentPortal.url}
              external
            >
              {f({ id: ROUTES.incidentPortal.name })}
            </LinkText>
          </GridItem>
          <GridItem small={12} medium={3}>
            <a
              href="https://www.facebook.com/entur.org/"
              className="omtur-footer__icon-link"
            >
              <FacebookIcon
                inline
                aria-label={`${f({ id: 'footer.linkTo' })} Facebook`}
              />
            </a>
            <a
              href="https://www.instagram.com/entur_as/"
              className="omtur-footer__icon-link"
            >
              <InstagramIcon
                inline
                aria-label={`${f({ id: 'footer.linkTo' })} Instagram`}
              />
            </a>
            <a
              href="https://twitter.com/entur_as"
              className="omtur-footer__icon-link"
            >
              <TwitterIcon
                inline
                aria-label={`${f({ id: 'footer.linkTo' })} X/Twitter`}
              />
            </a>
            <a
              href="https://www.linkedin.com/company/entur-as/"
              className="omtur-footer__icon-link"
            >
              <LinkedinIcon
                inline
                aria-label={`${f({ id: 'footer.linkTo' })} LinkedIn`}
              />
            </a>
          </GridItem>
        </GridContainer>
      </div>
    </Contrast>
  );
};
